import { useEffect, useMemo, useRef, useState } from 'react'
import type { ChangeEvent } from 'react'
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import { Avatar, Button } from '../ui'
import { storage } from '@/core/firebase'

interface TrainerProfilePhotoUploadProps {
  trainerId: string
  value: string
  onChange: (nextValue: string) => void
}

export function TrainerProfilePhotoUpload({
  trainerId,
  value,
  onChange,
}: TrainerProfilePhotoUploadProps) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [uploading, setUploading] = useState(false)
  const [urlInput, setUrlInput] = useState(value)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setUrlInput(value)
  }, [value])

  const fallback = useMemo(() => {
    const pathParts = value.split('/').filter(Boolean)
    return pathParts[pathParts.length - 1] || 'Trainer'
  }, [value])

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setUploading(true)
    setError(null)

    try {
      const extension = file.name.split('.').pop() || 'jpg'
      const storagePath = `trainer_profiles/${trainerId}/profile-${Date.now()}.${extension}`
      const storageRef = ref(storage, storagePath)
      await uploadBytes(storageRef, file, {
        contentType: file.type || 'image/jpeg',
      })
      const downloadURL = await getDownloadURL(storageRef)
      setUrlInput(downloadURL)
      onChange(downloadURL)
    } catch (uploadError) {
      console.error('Failed to upload trainer profile photo:', uploadError)
      setError('Failed to upload image. Try a direct image URL instead.')
    } finally {
      setUploading(false)
      event.target.value = ''
    }
  }

  const applyUrl = () => {
    const trimmed = urlInput.trim()
    onChange(trimmed)
    setError(null)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Avatar
          src={value || undefined}
          alt="Trainer profile photo"
          fallback={fallback}
          size="xl"
          className="w-20 h-20 text-xl"
        />
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => inputRef.current?.click()}
              isLoading={uploading}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Photo'}
            </Button>
            {value && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setUrlInput('')
                  onChange('')
                  setError(null)
                }}
              >
                Remove
              </Button>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-slate-500">
            Upload an image or paste a direct image URL.
          </p>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) => void handleFileChange(event)}
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
          Photo URL
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="url"
            value={urlInput}
            onChange={(event) => setUrlInput(event.target.value)}
            placeholder="https://example.com/photo.jpg"
            className="form-field flex-1"
          />
          <Button type="button" variant="secondary" onClick={applyUrl}>
            Use URL
          </Button>
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  )
}
